/**
 * =============================================================================
 * AI SPOTLIGHT WIDGET COMPONENT - DASHBOARD DESIGN SYSTEM
 * =============================================================================
 * 
 * AI intelligence module matching Top Stories card hierarchy:
 * - Label: 10px uppercase muted
 * - Heading: 15px semibold
 * - Action: 12px muted
 * - Content-sized container (no excessive whitespace)
 */

import { Box, Group, Text } from '@mantine/core';
import { Sparkle } from '@phosphor-icons/react';
import { memo } from 'react';
import type { AISpotlightWidgetProps } from '../../types';
import { AISpotlightCard } from '../AISpotlightCard';
import SystemActionCards from './SystemActionCards';

// =============================================================================
// DESIGN TOKENS - Matching WatchCard/Top Stories hierarchy
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 20,
} as const;

// Spotlight container - subtle glow effect
const spotlightStyles = {
    boxShadow: `
        0 1px 3px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(0, 0, 0, 0.03),
        0 0 0 1px rgba(59, 130, 246, 0.12),
        0 0 12px rgba(59, 130, 246, 0.06)
    `,
    background: 'linear-gradient(135deg, #FFFFFF 0%, rgba(248, 250, 255, 1) 100%)',
    borderRadius: 12,
} as const;

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const AISpotlightWidget: React.FC<AISpotlightWidgetProps> = memo(({
    tagline,
    config,
    className = '',
}) => {
    const { summary, cards, systemActions } = config;
    
    if (!cards || cards.length === 0) {
        return null;
    }
    
    return (
        <Box className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {/* Section Header - matches Top Stories (18px) */}
            {tagline && (
                <Group style={{ gap: spacing.xs }}>
                    <Sparkle 
                        size={14} 
                        weight="fill" 
                        style={{ 
                            color: 'rgb(59, 130, 246)',
                            filter: 'drop-shadow(0 0 2px rgba(59, 130, 246, 0.3))',
                        }}
                    />
                    <Text 
                        style={{ 
                            fontSize: 18,
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: 'rgb(26, 32, 44)',
                        }}
                    >
                        {tagline}
                    </Text>
                </Group>
            )}
            
            {/* Spotlight Container - content-sized, not flex-stretched */}
            <Box
                style={{
                    background: spotlightStyles.background,
                    borderRadius: spotlightStyles.borderRadius,
                    boxShadow: spotlightStyles.boxShadow,
                    padding: spacing.md,
                }}
            >
                {/* Summary - 12px muted */}
                <Text 
                    style={{ 
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: 'rgb(100, 116, 139)',
                        marginBottom: spacing.sm,
                    }}
                >
                    {summary}
                </Text>
                
                {/* 3-Column Grid */}
                <Box 
                    style={{ 
                        display: 'grid',
                        // Three across when there is room; cards wrap rather than overflow
                        // once the column is too narrow for three.
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: spacing.md,
                    }}
                >
                    {cards.slice(0, 3).map((cardConfig) => (
                        <AISpotlightCard
                            key={cardConfig.id}
                            config={cardConfig}
                        />
                    ))}
                </Box>
            </Box>

            {/* System-run actions — its own box right under the spotlight, same hierarchy */}
            {systemActions && <SystemActionCards config={systemActions} />}
        </Box>
    );
});

AISpotlightWidget.displayName = 'AISpotlightWidget';

export default AISpotlightWidget;
